import { put, call, getContext, select, cancel, take } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { publishOffer as publishOfferRequest } from '../../../../services/publishOffer';
import { IAppState } from '../../../types';
import {
  EOfferStatus,
  EPublishOfferErrorCode,
  OFFER_VALIDATION_FAIL,
  OFFER_VALIDATION_SUCCESS,
  publishOfferFail,
  publishOfferStart,
  publishOfferSuccess,
  PUBLISH_OFFER_FAIL,
  PUBLISH_OFFER_START,
  PUBLISH_OFFER_SUCCESS,
  TPublishOfferAction,
  updateOffer,
} from '../../../reducers';
import { clone } from 'ramda';
import { getPublishParams, getQuery, mapPublishTerms } from './utils';
import { selectIsArchived, selectIsFromXml, selectIsDealRent } from '../../../../selectors/offer';
import { getPublishTerms } from '../../checkoutInfo/checkService/utils/getPublishTerms';
import {
  selectActivePackage,
  selectActivePeriod,
  selectActivePlacingType,
  selectActiveRecommendedPackage,
  selectActiveService,
} from '../../../../selectors/publishTerms';
import { selectIsAuctionEnabled } from '../../../../selectors/auction';
import { selectIsAutoProlongAvailable, selectIsAutoProlongChecked } from '../../../../selectors/checkoutInfo';
import { sendEvent } from '../../../../utils';
import { checkPublish } from '../checkPublish';
import { selectIsPublishToHiddenBase } from '../../../../selectors/publishAvailable';
import { TValidationAction } from './types';
import { selectConfigValue } from '../../../../selectors/config';

export function* publishOffer(action: TPublishOfferAction) {
  const { isPremoderationSkip = false } = action.payload;

  yield put(publishOfferStart());

  sendEvent(PUBLISH_OFFER_START, { isPremoderationSkip });

  const validationAction: TValidationAction = yield take([OFFER_VALIDATION_SUCCESS, OFFER_VALIDATION_FAIL]);

  if (validationAction.type === OFFER_VALIDATION_FAIL) {
    const validationError = {
      errorCode: EPublishOfferErrorCode.validationError,
      errorMessage: null,
      errors: null,
    };

    yield put(publishOfferFail(validationError));

    yield cancel();

    return;
  }

  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const state: IAppState = yield select();

  const service = selectActiveService(state);
  const servicePackage = selectActivePackage(state);
  const period = selectActivePeriod(state);
  const placingType = selectActivePlacingType(state);
  const recommendedPackage = selectActiveRecommendedPackage(state);
  const isAuctionEnabled = selectIsAuctionEnabled(state);
  const isAutoprolongAvailable = selectIsAutoProlongAvailable(state);
  const isAutoprolongChecked = selectIsAutoProlongChecked(state);
  const isArchived = selectIsArchived(state);
  const isFromXml = selectIsFromXml(state);
  const isDealRent = selectIsDealRent(state);
  const isPublishToHiddenBase = selectIsPublishToHiddenBase(state);

  const { auction, calltracking } = state;

  const subdomain = selectConfigValue('subdomain', state) as string;

  const offer = clone(state.offer);

  offer.status = EOfferStatus.published;

  const terms = getPublishTerms({
    service,
    servicePackage,
    recommendedPackage,
    period,
    placingType,
    auction,
    isAuctionEnabled,
    calltracking,
  });

  // При публикации в скрытую базу, check-publish дергать не нужно
  if (!isPublishToHiddenBase) {
    const checkPublishResult = yield call(checkPublish, terms);

    if (!checkPublishResult.success) {
      yield put(publishOfferFail(checkPublishResult.payload));

      sendEvent(PUBLISH_OFFER_FAIL, { statusCode: 0, payload: checkPublishResult.payload });

      yield cancel();

      return;
    }
  }

  const publishTerms = mapPublishTerms({ isAutoprolongAvailable, isAutoprolongChecked, terms });

  const parameters = getPublishParams({
    offer,
    calltracking,
    publishTerms,
  });

  const query = getQuery({
    isArchived,
    isFromXml,
    isDealRent,
  });

  const { success, statusCode, payload } = yield call(publishOfferRequest, {
    httpApi,
    logger,
    parameters,
    query,
    subdomain,
  });

  if (success) {
    yield put(publishOfferSuccess());
    yield put(updateOffer(payload));

    sendEvent(PUBLISH_OFFER_SUCCESS, payload);
  } else {
    yield put(publishOfferFail(payload));

    sendEvent(PUBLISH_OFFER_FAIL, { statusCode, payload });
  }
}
